import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 0,
  boxShadow: 24,
  px: 4,
  pb: 4,
  pt: 4,
};

const EditProfileModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [institution, setInstitution] = useState("");
  const [about, setAbout] = useState("");
  const [xlink, setXlink] = useState("");
  const [linkedin, setLinkedin] = useState("");

  // Populate fields whenever modal opens
  useEffect(() => {
    if (open && user) {
      setFirstName(user.firstname || "");
      setLastName(user.lastname || "");
      setEmail(user.email || "");
      setInstitution(user.institution || "");
      setAbout(user.about || "");
      setXlink(user.xlink || "");
      setLinkedin(user.linkedin || "");
    }
  }, [user, open]);

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setInstitution("");
    setAbout("");
    setXlink("");
    setLinkedin("");
    onClose();
  };

  const handleUpdate = () => {
    navigate("/account/1"); // demo redirect
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" } }}
      sx={{ outline: "none" }}
    >
      <Box
        sx={{
          ...modalStyle,
          outline: "none",
          "&:focus": { outline: "none" },
          "&:focus-visible": { outline: "none" },
        }}
        tabIndex={-1}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 10, padding: "4px", cursor: "pointer" }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: "12px",
            left: 0,
            width: "100%",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            fontSize: "32px",
            fontWeight: 400,
            color: "#142434",
          }}
        >
          Edit Profile
        </Typography>

        {/* Divider */}
        <Divider sx={{ position: "absolute", top: "70px", left: 0, width: "100%", borderColor: "#489596" }} />

        {/* Input fields */}
        <Box display="flex" flexDirection="column" gap={2} mt={7}>
          {/* First and Last Name on same line */}
          <Box display="flex" gap={2}>
            <Box flex={1}>
              <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
                First Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14 } }}
              />
            </Box>
            <Box flex={1}>
              <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
                Last Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14 } }}
              />
            </Box>
          </Box>

          {/* Email (disabled with grey background) */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={email}
              disabled
              InputProps={{
                style: { fontFamily: "PT Sans", fontSize: 14, backgroundColor: "#f5f5f5" },
              }}
            />
          </Box>

          {/* Institution */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
              Institution/Company
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14 } }}
            />
          </Box>

          {/* About Me (multiline) */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
              About Me
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14 } }}
            />
          </Box>

          {/* X (Twitter) */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
              X (Twitter)
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={xlink}
              onChange={(e) => setXlink(e.target.value)}
              inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14 } }}
            />
          </Box>

          {/* LinkedIn */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}>
              LinkedIn
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14 } }}
            />
          </Box>

          {/* Update button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{
                width: "190px",
                height: "42px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "18px",
                fontWeight: 600,
                color: "#FFFFFF",
                boxShadow: "none",
                backgroundColor: "#489596",
                "&:hover": { backgroundColor: "#3a7b7f", boxShadow: "none" },
                textTransform: "none",
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditProfileModal;
